import React, { useCallback, useMemo, useState } from 'react';

import { makeStyles } from 'tss-react/mui';

import { SubAgentCard } from './SubAgentCard';
import type { SubAgentRow } from './useSubAgentRows';
import type { ChatAssistantMessage } from '../../../chat-types';
import type { ChatDocument } from '../../ChatPanelAssistantMessage';

const useStyles = makeStyles()(theme => ({
  list: {
    '& > *:not(:last-child)': {
      borderBottom: `1px solid ${theme.appColors.rule}`,
    },
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderTop: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function SubAgentCardList({
  documentInView,
  message,
  rows,
  threadId,
}: {
  documentInView: ChatDocument | null;
  message: ChatAssistantMessage;
  rows: SubAgentRow[];
  threadId: UUID;
}) {
  const { classes } = useStyles();
  const [expandedNodeIds, setExpandedNodeIds] = useState<number[]>([]);
  const streamingNodeIds = useMemo(
    () =>
      rows
        .filter(row => row.status === 'started' && row.nodeText.length > 0)
        .map(row => row.nodeId),
    [rows]
  );

  const handleToggle = useCallback(
    (row: SubAgentRow) => {
      if (row.nodeText.length === 0) return;

      setExpandedNodeIds(current => {
        const otherExpandedNodeIds = current.filter(nodeId => nodeId !== row.nodeId);
        if (otherExpandedNodeIds.length !== current.length) return otherExpandedNodeIds;

        if (row.status === 'started') {
          return [
            ...otherExpandedNodeIds.filter(nodeId => !streamingNodeIds.includes(nodeId)),
            row.nodeId,
          ];
        }

        return [...otherExpandedNodeIds, row.nodeId];
      });
    },
    [streamingNodeIds]
  );

  return (
    <div className={classes.list}>
      {rows.map(row => (
        <SubAgentCard
          key={row.nodeId}
          documentInView={documentInView}
          expanded={expandedNodeIds.includes(row.nodeId)}
          message={message}
          row={row}
          threadId={threadId}
          onToggle={() => handleToggle(row)}
        />
      ))}
    </div>
  );
}
